import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, TrendingUp, Wallet, Cpu, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/LanguageContext";

const icons = [TrendingUp, Heart, Wallet, Cpu];
const ids = ["attraction", "health", "efficiency", "ai"];

function TrendCard({ id, icon: Icon, number, item, isActive, onClick }: {
  id: string;
  icon: React.ElementType;
  number: string;
  item: { title: string; subtitle: string; stats: { value: string; label: string }[]; description: string; insight: string };
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <motion.div
      layout
      className={cn(
        "relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300",
        isActive ? "bg-primary text-primary-foreground shadow-xl" : "bg-card border border-border hover:shadow-md"
      )}
      onClick={onClick}
    >
      <div className="p-6">
        <div className="flex items-center gap-4 mb-4">
          <span className={cn("text-4xl font-bold", "text-accent")}>{number}</span>
          <div className={cn("p-3 rounded-xl", isActive ? "bg-primary-foreground/10" : "bg-accent/10")}>
            <Icon className="h-6 w-6 text-accent" />
          </div>
        </div>
        <h3 className={cn("text-xl font-bold mb-2", isActive ? "text-primary-foreground" : "text-foreground")}>
          {item.title}
        </h3>
        <p className={cn("text-sm flex items-center gap-1", isActive ? "text-primary-foreground/70" : "text-muted-foreground")}>
          {item.subtitle}
          <ChevronRight className={cn("h-4 w-4 transition-transform", isActive ? "rotate-90" : "")} />
        </p>
      </div>

      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="px-6 pb-6"
          >
            <div className="grid grid-cols-2 gap-4 mb-4">
              {item.stats.map((stat, idx) => (
                <div key={idx} className="bg-primary-foreground/10 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-accent">{stat.value}</div>
                  <div className="text-xs text-primary-foreground/70">{stat.label}</div>
                </div>
              ))}
            </div>
            <p className="text-sm text-primary-foreground/80 mb-4">{item.description}</p>
            <blockquote className="border-l-2 border-accent pl-3 text-sm italic text-primary-foreground/70">
              {item.insight}
            </blockquote>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function TrendCards() {
  const [activeId, setActiveId] = useState<string>("attraction");
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-background">
      <div className="container px-4">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 mb-4 text-sm font-medium rounded-full bg-accent/10 text-accent">
            {t.trends.badge}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t.trends.title}{" "}
            <span className="font-serif italic">{t.trends.titleItalic}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.trends.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {ids.map((id, index) => (
            <motion.div
              key={id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <TrendCard
                id={id}
                icon={icons[index]}
                number={`0${index + 1}`}
                item={t.trends.items[index]}
                isActive={activeId === id}
                onClick={() => setActiveId(id)}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
